import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Panel de marca de las pantallas sin sesion (login, registro y recuperar
 * contrasena). Recrea la portada del manual de marca de Medivelius: bloques
 * magenta, azul y navy con los chevrones blancos, y el mensaje de la marca.
 *
 * Usa solo elementos del manual (sin fotografia). En escritorio ocupa la mitad
 * izquierda y el login compartido se corre a la derecha (medivelius-shell.css);
 * por debajo de 1024px queda solo una franja con los colores de la marca.
 */
@Component({
  selector: 'app-auth-brand-panel',
  standalone: true,
  imports: [NgTemplateOutlet, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <aside class="panel" aria-hidden="true">
      <div class="copy">
        <p class="kicker">{{ 'auth.brand.kicker' | translate }}</p>
        <p class="headline">{{ 'auth.brand.headline' | translate }}</p>
        <span class="rule"></span>
        <p class="caption">{{ 'auth.brand.caption' | translate }}</p>
      </div>

      <div class="blocks">
        <div class="tint">
          <ng-container *ngTemplateOutlet="chevron; context: { color: 'var(--mv-brand-blue)' }" />
        </div>
        <div class="magenta">
          @for (i of [0, 1, 2]; track i) {
            <ng-container *ngTemplateOutlet="chevron; context: { color: '#fff' }" />
          }
        </div>
        <div class="blue"></div>
        <div class="navy"></div>
      </div>
    </aside>

    <div class="strip" aria-hidden="true"></div>

    <ng-template #chevron let-color="color">
      <svg class="chevron" viewBox="0 0 100 60">
        <polyline points="10,8 50,48 90,8" fill="none" [attr.stroke]="color" stroke-width="16" />
      </svg>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .panel {
        position: fixed;
        inset: 0 auto 0 0;
        z-index: 0;
        display: none;
        width: 50vw;
        background: var(--mv-surface);
        overflow: hidden;
      }

      @media (min-width: 1024px) {
        .panel {
          display: grid;
          grid-template-columns: minmax(0, 56%) minmax(0, 44%);
        }

        .strip {
          display: none;
        }
      }

      /* ---------- Mensaje ---------- */

      .copy {
        align-self: center;
        padding: 0 clamp(24px, 3vw, 48px) 0 clamp(32px, 5vw, 80px);
        font-family: var(--mv-font-sans);
      }

      .kicker {
        margin: 0 0 20px;
        white-space: nowrap;
        font-size: 12px;
        letter-spacing: 3px;
        text-transform: uppercase;
        color: var(--mv-brand-magenta);
      }

      /* Roboto Light en grande: lo que hace que se vea Medivelius. */
      .headline {
        margin: 0;
        font-size: clamp(28px, 2.4vw, 36px);
        line-height: 1.15;
        font-weight: 300;
        letter-spacing: 0.5px;
        color: var(--mv-ink);
      }

      .rule {
        display: block;
        width: 96px;
        height: 3px;
        margin: 24px 0;
        background: var(--mv-navy);
      }

      .caption {
        margin: 0;
        max-width: 28ch;
        font-size: 14px;
        line-height: 22px;
        color: var(--mv-ink-muted);
      }

      /* ---------- Bloques de la portada del manual ---------- */

      .blocks {
        position: relative;
        display: grid;
        grid-template-columns: 1fr 1.15fr 0.85fr;
        grid-template-rows: 64% 36%;
      }

      .tint {
        grid-column: 1;
        grid-row: 1 / 3;
        margin-top: 30vh;
        display: flex;
        justify-content: center;
        align-items: flex-end;
        padding-bottom: 16vh;
        background: var(--mv-brand-magenta-tint);
      }

      .magenta {
        grid-column: 2;
        grid-row: 1 / 3;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4vh;
        padding-top: 18vh;
        background: var(--mv-brand-magenta);
      }

      .blue {
        grid-column: 3;
        grid-row: 1;
        background: var(--mv-brand-blue);
      }

      .navy {
        grid-column: 3;
        grid-row: 2;
        background: var(--mv-navy);
      }

      .chevron {
        width: clamp(44px, 4.5vw, 80px);
        height: auto;
      }

      /* ---------- Movil y tablet: franja con los colores de la marca ---------- */

      .strip {
        position: fixed;
        inset: 0 0 auto 0;
        z-index: 2;
        height: 6px;
        background: linear-gradient(
          90deg,
          var(--mv-brand-magenta-tint) 0 25%,
          var(--mv-brand-magenta) 25% 60%,
          var(--mv-brand-blue) 60% 85%,
          var(--mv-navy) 85% 100%
        );
      }

      @media (prefers-reduced-motion: no-preference) {
        .blocks > div {
          animation: mv-rise 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) both;
        }

        .magenta {
          animation-delay: 0.08s;
        }

        .blue,
        .navy {
          animation-delay: 0.16s;
        }
      }

      @keyframes mv-rise {
        from {
          opacity: 0;
          transform: translateY(24px);
        }
      }
    `,
  ],
})
export class AuthBrandPanelComponent {}
